import * as React from "react";
import * as toastr from "toastr";
import { IBookOfMindSpeechCacheStats } from "motw_core/DTOs/IBookOfMindSpeechCacheStats";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import MotwLogicServer from "source/app/services/External/LogicServer/MotwLogicServer";
import {
    GetClientErrorDetails,
    LogClientDiagnostic,
} from "source/app/services/ClientDiagnostics";
import ChevronPanel from "source/app/presentation/Shared/ChevronPanel/ChevronPanel";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { ERPGIcon } from "motw_core/UI/Icons/ERPGIcon";

export function BookOfMindSpeechCacheStats() {
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const [stats, setStats] = React.useState<IBookOfMindSpeechCacheStats>();
    const [error, setError] = React.useState<string>();
    const [lastRefresh, setLastRefresh] = React.useState<string>();

    const loadStats = React.useCallback(async () => {
        if (isLoading) {
            return;
        }

        setIsLoading(true);
        setError(undefined);
        try {
            const result = await MotwLogicServer.GetBookOfMindSpeechCacheStatsAsync();
            if (!result || typeof result !== "object") {
                throw new Error("Book of Mind cache statistics were unavailable.");
            }
            setStats(result);
            setLastRefresh(new Date().toISOString());
        } catch (loadError) {
            const message = "Unable to load Book of Mind audio cache statistics.";
            setError(message);
            toastr.error(message, "Book of Mind audio cache");
            LogClientDiagnostic(
                "book-of-mind-cache-stats",
                "load-error",
                { error: GetClientErrorDetails(loadError) },
                "error"
            );
        } finally {
            setIsLoading(false);
        }
    }, [isLoading]);

    const handlePanelClick = () => {
        const willExpand = !isExpanded;
        setIsExpanded(willExpand);
        if (willExpand && !stats && !isLoading) {
            void loadStats();
        }
    };

    return (
        <ChevronPanel
            id="UnderDevelopment_BookOfMindSpeechCacheStats"
            label="Book of Mind audio cache"
            icon={ERPGIcon.book}
            dynamicOpacity={false}
            whenClicked={handlePanelClick}
            withClasses={StyleClassAuto({ textAlign: "center" })}
        >
            <div className={StyleClassAuto({ padding: "8px" })}>
                <IconWithText
                    iconSuffix={EGlyphicon.refresh}
                    whenClicked={() => void loadStats()}
                    displayAsBlock={false}
                >
                    Refresh stats
                </IconWithText>
                {lastRefresh && (
                    <div className={StyleClassAuto({ fontSize: "12px", marginTop: "6px" })}>
                        Last refresh: {FormatTimestamp(lastRefresh)}
                    </div>
                )}
                {isLoading && <div>Loading cache statistics...</div>}
                {error && <div className={ErrorStyle}> {error}</div>}
                {stats && <StatsBody stats={stats} />}
            </div>
        </ChevronPanel>
    );
}

function StatsBody({ stats }: { stats: IBookOfMindSpeechCacheStats }) {
    if (!stats.available) {
        return (
            <div className={ErrorStyle}>
                Cache statistics are currently unavailable.
                {stats.warning ? ` ${stats.warning}` : ""}
            </div>
        );
    }

    if (stats.storage.totalObjects === 0) {
        return <div>No cached Book of Mind audio objects yet.</div>;
    }

    return (
        <div className={StatsStyle}>
            <div>
                Prefix: <code>{stats.cachePrefix}</code> · Version: {stats.cacheVersion} · Environment: {stats.environment}
            </div>
            {stats.warning && <div className={WarningStyle}>Partial data: {stats.warning}</div>}
            <h4>Storage</h4>
            <div>{stats.storage.totalObjects} objects · {stats.storage.quoteObjects} quotes · {stats.storage.titleObjects} titles</div>
            <div>{FormatBytes(stats.storage.totalBytes)} total · {FormatBytes(stats.storage.averageBytes)} average</div>
            <div>Uploads: {FormatTimestamp(stats.storage.oldestUploadTimestamp)} → {FormatTimestamp(stats.storage.newestUploadTimestamp)}</div>
            <h4>Runtime (this logic-server instance)</h4>
            <div>Hits {stats.runtime.hits} · Misses {stats.runtime.misses} · WaveNet fallbacks {stats.runtime.googleFallbacks}</div>
            <div>Uploads {stats.runtime.uploads} · Upload failures {stats.runtime.uploadFailures} · B2 lookup failures {stats.runtime.lookupFailures}</div>
            <h4>Variants</h4>
            <StatsTable headers={["Type", "Voice", "Rate", "Pitch", "Objects", "Bytes"]}>
                {stats.variants.map((variant) => (
                    <tr key={`${variant.segmentType}-${variant.voiceName}-${variant.speakingRate}-${variant.pitch}`}>
                        <td>{variant.segmentType}</td><td>{variant.voiceName}</td><td>{variant.speakingRate}</td><td>{variant.pitch}</td><td>{variant.objectCount}</td><td>{FormatBytes(variant.totalBytes)}</td>
                    </tr>
                ))}
            </StatsTable>
            <h4>Quote coverage</h4>
            <StatsTable headers={["Voice", "Rate", "Pitch", "Cached", "Missing", "%"]}>
                {stats.coverage.map((coverage) => (
                    <tr key={`${coverage.voiceName}-${coverage.speakingRate}-${coverage.pitch}`}>
                        <td>{coverage.voiceName}</td><td>{coverage.speakingRate}</td><td>{coverage.pitch}</td><td>{coverage.cachedQuotes}/{coverage.totalQuotes}</td><td>{coverage.missingQuotes}</td><td>{coverage.coveragePercent.toFixed(1)}</td>
                    </tr>
                ))}
            </StatsTable>
        </div>
    );
}

function StatsTable({ headers, children }: { headers: string[]; children: React.ReactNode }) {
    return (
        <table className={TableStyle}>
            <thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead>
            <tbody>{children}</tbody>
        </table>
    );
}

function FormatBytes(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KiB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MiB`;
}

function FormatTimestamp(timestamp?: string) {
    return timestamp ? new Date(timestamp).toLocaleString() : "unknown";
}

const ErrorStyle = StyleClassAuto({ color: "#ef9a9a", marginTop: "8px" });
const WarningStyle = StyleClassAuto({ color: "#f1d18a", marginTop: "8px" });
const StatsStyle = StyleClassAuto({ fontSize: "12px", lineHeight: 1.5, marginTop: "8px", textAlign: "left" });
const TableStyle = StyleClassAuto({ borderCollapse: "collapse", fontSize: "11px", margin: "8px 0", width: "100%" });
